/**
 * Validadores de datos
 */

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Valida un email
 */
export function validateEmail(email: string): ValidationResult {
  const errors: string[] = [];
  
  if (!email) {
    errors.push('Email es requerido');
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errors.push('Formato de email inválido');
    }
    if (email.length > 254) {
      errors.push('Email demasiado largo');
    }
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Valida un teléfono
 */
export function validatePhone(phone: string): ValidationResult {
  const errors: string[] = [];
  
  if (!phone) {
    return { valid: true, errors: [] }; // Phone es opcional
  }
  
  const phoneRegex = /^\+?[0-9]{8,15}$/;
  if (!phoneRegex.test(phone.replace(/[\s-]/g, ''))) {
    errors.push('Formato de teléfono inválido');
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Valida un nombre
 */
export function validateName(name: string, fieldName = 'Nombre'): ValidationResult {
  const errors: string[] = [];
  
  if (!name || name.trim().length === 0) {
    errors.push(`${fieldName} es requerido`);
  } else {
    if (name.length < 2) {
      errors.push(`${fieldName} debe tener al menos 2 caracteres`);
    }
    if (name.length > 100) {
      errors.push(`${fieldName} no puede exceder 100 caracteres`);
    }
    // Prevenir caracteres peligrosos
    if (/<script|javascript:|on\w+=/i.test(name)) {
      errors.push(`${fieldName} contiene caracteres no permitidos`);
    }
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Valida un monto monetario
 */
export function validateAmount(amount: number | string): ValidationResult {
  const errors: string[] = [];
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(num)) {
    errors.push('Monto inválido');
  } else {
    if (num < 0) {
      errors.push('El monto no puede ser negativo');
    }
    if (num > 999999999) {
      errors.push('El monto excede el límite permitido');
    }
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Valida un ID (UUID o similar)
 */
export function validateId(id: string): ValidationResult {
  const errors: string[] = [];
  
  if (!id) {
    errors.push('ID es requerido');
  } else {
    // Permitir UUIDs y IDs alfanuméricos
    const idRegex = /^[a-zA-Z0-9_-]{1,64}$/;
    if (!idRegex.test(id)) {
      errors.push('Formato de ID inválido');
    }
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Valida un CUIT/CUIL argentino
 */
export function validateCuit(cuit: string): ValidationResult {
  const errors: string[] = [];
  
  if (!cuit) {
    return { valid: true, errors: [] }; // CUIT es opcional
  }
  
  const cleaned = cuit.replace(/[-]/g, '');
  
  if (!/^\d{11}$/.test(cleaned)) {
    errors.push('CUIT debe tener 11 dígitos');
    return { valid: false, errors };
  }
  
  // Validar dígito verificador
  const multipliers = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * multipliers[i];
  }
  const mod = sum % 11;
  const verifier = mod === 0 ? 0 : mod === 1 ? 9 : 11 - mod;
  
  if (verifier !== parseInt(cleaned[10])) {
    errors.push('CUIT inválido');
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Valida datos de cliente
 */
export function validateClienteData(data: {
  nombre?: string;
  email?: string;
  telefono?: string;
}): ValidationResult {
  const errors: string[] = [];
  
  const nameResult = validateName(data.nombre || '');
  errors.push(...nameResult.errors);
  
  if (data.email) {
    const emailResult = validateEmail(data.email);
    errors.push(...emailResult.errors);
  }
  
  if (data.telefono) {
    const phoneResult = validatePhone(data.telefono);
    errors.push(...phoneResult.errors);
  }
  
  return { valid: errors.length === 0, errors };
}

/**
 * Valida datos de factura
 */
export function validateFacturaData(data: {
  tipo_cbte?: string;
  cliente_nombre?: string;
  cliente_doc_nro?: string;
  items?: Array<{ descripcion?: string; cantidad?: number; precio_unitario?: number }>;
}): ValidationResult {
  const errors: string[] = [];
  
  if (!data.tipo_cbte || !['A', 'B', 'C'].includes(data.tipo_cbte)) {
    errors.push('Tipo de comprobante inválido');
  }
  
  if (!data.items || data.items.length === 0) {
    errors.push('Debe incluir al menos un item');
  } else {
    data.items.forEach((item, index) => {
      if (!item.descripcion) {
        errors.push(`Item ${index + 1}: descripción requerida`);
      }
      if (!item.cantidad || item.cantidad <= 0) {
        errors.push(`Item ${index + 1}: cantidad inválida`);
      }
      if (item.precio_unitario === undefined || item.precio_unitario < 0) {
        errors.push(`Item ${index + 1}: precio inválido`);
      }
    });
  }
  
  return { valid: errors.length === 0, errors };
}
